/**
 * Seconds only, and not for want of a converter: `helpers/scheduling.ts`
 * writes its own `valueDuration` extensions as bare minutes with no
 * `system`/`code`. Reading one of those here would understate a value
 * 60-fold, so `parseDurationSeconds` demands `code: "s"` and reports the
 * rest as absent. Do not add unit conversion.
 *
 * Page-agnostic: must not import from `configs/<page>/`.
 */
import type { Duration } from "@medplum/fhirtypes";
import type { TFunction } from "i18next";

import type { DurationUnit } from "@/types/clinic-configuration";
import {
  DURATION_UNITS_ASCENDING,
  SECONDS_PER_DURATION_UNIT,
} from "@/constants/clinic-configuration.constants";
import { FHIR_UCUM } from "@/constants/fhir.constants";
import { isNumber } from "@/lib/utils/typeof";

const DURATION_UNITS_DESCENDING = [...DURATION_UNITS_ASCENDING].reverse();

const SMALLEST_DURATION_UNIT = DURATION_UNITS_ASCENDING[0];

/**
 * Zero is special-cased because it divides evenly by every unit, so the
 * largest-first rule below would render "no delay" as "0 days".
 */
export const resolveDurationUnit = (seconds: number): DurationUnit => {
  if (seconds === 0) {
    return SMALLEST_DURATION_UNIT;
  }

  return (
    DURATION_UNITS_DESCENDING.find(
      (unit) => seconds % SECONDS_PER_DURATION_UNIT[unit] === 0
    ) ?? SMALLEST_DURATION_UNIT
  );
};

/**
 * Never rounds: a value that is not a whole number of the resolved unit is
 * reported in seconds rather than shown as a duration it is not.
 *
 * The `t` calls are spelled out per unit rather than read from a record of
 * key strings, and that is load-bearing: i18next-scanner needs the literal
 * call to discover `count` before it will write each locale's
 * `_one`/`_other`/`_many` forms. The `// t('key')` comments used for dynamic
 * keys elsewhere carry no `count`, so a record collapses all three forms
 * into one flat key and drops the existing translations. Verified against a
 * record version with `pnpm i18n:scan`.
 *
 * The missing `default` is deliberate too: a new `DurationUnit` breaks this
 * switch's return type rather than rendering as a raw i18n key at runtime.
 */
export const formatDurationSeconds = (
  seconds: number,
  t: TFunction
): string => {
  const unit = resolveDurationUnit(seconds);
  const divisor = SECONDS_PER_DURATION_UNIT[unit];

  if (seconds % divisor !== 0) {
    return t("{{count}} seconds", { count: seconds });
  }

  const count = seconds / divisor;

  switch (unit) {
    case "day":
      return t("{{count}} days", { count });
    case "hour":
      return t("{{count}} hours", { count });
    case "minute":
      return t("{{count}} minutes", { count });
  }
};

/**
 * Side-effect free, so callers own their logging: pair it with
 * `isUnsupportedDurationUnit` to report a non-seconds value rather than
 * letting it read as merely absent.
 */
export const parseDurationSeconds = (
  duration: Duration | undefined
): number | undefined => {
  if (!duration || !isNumber(duration.value)) {
    return undefined;
  }

  return duration.code === FHIR_UCUM.SECONDS_CODE ? duration.value : undefined;
};

export const isUnsupportedDurationUnit = (
  duration: Duration | undefined
): boolean =>
  !!duration &&
  isNumber(duration.value) &&
  duration.code !== FHIR_UCUM.SECONDS_CODE;

export const buildDurationQuantity = (seconds: number): Duration => ({
  value: seconds,
  unit: FHIR_UCUM.SECONDS_CODE,
  system: FHIR_UCUM.SYSTEM,
  code: FHIR_UCUM.SECONDS_CODE,
});
